/**
 * Type-safe environment variables configuration for the Vite SPA.
 *
 * Vite only exposes variables prefixed `VITE_` to the client bundle via
 * `import.meta.env`. Server-only secrets (DATABASE_URL, BETTER_AUTH_SECRET,
 * OAuth client secrets, Resend keys, etc.) MUST NOT live here — they are
 * consumed exclusively by the backend (`server/src/infrastructure/auth/`).
 *
 * BUILD-TIME VARIABLES (set as Docker build args or via CI before `vite build`):
 * - VITE_BACKEND_URL, VITE_APP_URL
 *
 * All other values fall back to hardcoded defaults from `constants.ts`.
 */
import { DEFAULT_APP_URL, DISABLE_EVENT_DEBUG, TIMEZONE } from "./constants";

/**
 * Validated environment variable schema. Only `VITE_*` keys are exposed to
 * the browser bundle by Vite; everything else is filtered out at build time.
 */
export interface Env {
	readonly VITE_APP_URL?: string;
	readonly VITE_APP_VERSION?: string;
	// Build-time public variables (set via Docker args / CI / .env)
	readonly VITE_BACKEND_URL?: string;
	readonly VITE_BING_SITE_VERIFICATION?: string;
	readonly VITE_DISABLE_EVENT_DEBUG?: string;
	// Debug flags (hardcoded in constants, can be overridden)
	readonly VITE_EVENT_DEBUG?: string;
	// Social auth (OAuth client IDs are public — presence enables the UI button)
	readonly VITE_GOOGLE_CLIENT_ID?: string;
	// Google Maps JavaScript API key (public, referer-restricted). Presence
	// enables the interactive map location picker.
	readonly VITE_GOOGLE_MAPS_API_KEY?: string;
	// Google Maps Map ID (public). Required for Advanced Markers / vector maps;
	// falls back to Google's `DEMO_MAP_ID` when unset.
	readonly VITE_GOOGLE_MAPS_MAP_ID?: string;
	// Site verification meta tags (public)
	readonly VITE_GOOGLE_SITE_VERIFICATION?: string;
	readonly VITE_LOG_LEVEL?: string;
	readonly VITE_MICROSOFT_CLIENT_ID?: string;
	readonly VITE_SENTRY_DSN?: string;
	readonly VITE_SENTRY_DSN_DEV?: string;
	readonly VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
	readonly VITE_SYSTEM_AGENT_NAME?: string;
	readonly VITE_THEME_MANIFEST_VERSION?: string;
	readonly VITE_TIMEZONE?: string;
	readonly VITE_TURNSTILE_SITE_KEY?: string;
	readonly VITE_VAPID_PUBLIC_KEY?: string;
}

/**
 * Enumerated list of the environment variable keys we read. Used by
 * {@link EnvConfig} to copy values out of `import.meta.env` without
 * relying on an `as` cast for the index signature.
 */
const ENV_KEYS = [
	"VITE_BACKEND_URL",
	"VITE_APP_URL",
	"VITE_APP_VERSION",
	"VITE_EVENT_DEBUG",
	"VITE_DISABLE_EVENT_DEBUG",
	"VITE_TIMEZONE",
	"VITE_LOG_LEVEL",
	"VITE_GOOGLE_CLIENT_ID",
	"VITE_GOOGLE_MAPS_API_KEY",
	"VITE_GOOGLE_MAPS_MAP_ID",
	"VITE_MICROSOFT_CLIENT_ID",
	"VITE_TURNSTILE_SITE_KEY",
	"VITE_VAPID_PUBLIC_KEY",
	"VITE_GOOGLE_SITE_VERIFICATION",
	"VITE_BING_SITE_VERIFICATION",
	"VITE_SENTRY_DSN",
	"VITE_SENTRY_DSN_DEV",
	"VITE_SENTRY_TRACES_SAMPLE_RATE",
	"VITE_SYSTEM_AGENT_NAME",
	"VITE_THEME_MANIFEST_VERSION",
] as const satisfies ReadonlyArray<keyof Env>;

/**
 * Narrow shape of `import.meta.env`. Vite injects `MODE`, `DEV`, `PROD`,
 * `SSR`, `BASE_URL` plus any `VITE_*` vars declared at build time. We type
 * it loosely (`unknown` indexer) so individual lookups don't blanket-cast.
 */
interface ImportMetaEnvLike {
	readonly DEV?: boolean;
	readonly MODE?: string;
	readonly PROD?: boolean;
	readonly [key: string]: unknown;
}

function readImportMetaEnv(): ImportMetaEnvLike {
	// `import.meta.env` must be spelled out verbatim: Vite statically rewrites
	// only the exact `import.meta.env` expression (dev and build). Aliasing
	// `import.meta` first (`const meta = import.meta`) defeats the rewrite, so
	// `.env` is undefined at runtime and every VITE_* var silently reads empty.
	// The guard keeps non-Vite tooling (Node test runners) from crashing.
	try {
		const env = import.meta.env as unknown as ImportMetaEnvLike | undefined;
		return env ?? {};
	} catch {
		return {};
	}
}

/**
 * Type-safe environment variable getter
 * Use this instead of `import.meta.env` directly.
 */
class EnvConfig {
	private readonly env: Env;
	private readonly metaMode: string;
	private readonly metaDev: boolean;

	constructor() {
		const source = readImportMetaEnv();
		const env: { -readonly [K in keyof Env]?: string } = {};
		for (const key of ENV_KEYS) {
			const value = source[key];
			if (typeof value === "string" && value !== "") {
				env[key] = value;
			}
		}
		this.env = env;
		this.metaMode =
			typeof source.MODE === "string" ? source.MODE : "production";
		this.metaDev =
			typeof source.DEV === "boolean"
				? source.DEV
				: this.metaMode === "development";
	}

	/**
	 * Get a required environment variable
	 * Throws if the variable is not set
	 */
	require(key: keyof Env): string {
		const value = this.env[key];
		if (value === undefined || value === "") {
			throw new Error(
				`Required environment variable ${String(key)} is not set`,
			);
		}
		return value;
	}

	/**
	 * Get an optional environment variable
	 * Returns undefined if not set
	 */
	get(key: keyof Env): string | undefined {
		return this.env[key];
	}

	/**
	 * Get an optional environment variable with a default value
	 */
	getOrDefault(key: keyof Env, defaultValue: string): string {
		return this.env[key] ?? defaultValue;
	}

	/**
	 * Check if an environment variable is set and truthy
	 */
	has(key: keyof Env): boolean {
		const value = this.env[key];
		return value !== undefined && value !== "";
	}

	/**
	 * Get a boolean environment variable
	 * Returns true if value is 'true', '1', or 'yes' (case-insensitive)
	 */
	getBoolean(key: keyof Env, defaultValue = false): boolean {
		const value = this.env[key];
		if (value === undefined || value === "") {
			return defaultValue;
		}
		const normalized = value.toLowerCase().trim();
		return normalized === "true" || normalized === "1" || normalized === "yes";
	}

	/**
	 * Get all environment variables (for debugging)
	 */
	getAll(): Readonly<Env> {
		return { ...this.env };
	}

	/**
	 * Vite build mode ("development" | "production" | "test" | custom mode).
	 * Mirrors `import.meta.env.MODE`.
	 */
	get mode(): string {
		return this.metaMode;
	}

	/**
	 * Convenience boolean — `true` when running `vite dev` (or any non-prod
	 * mode that flips `import.meta.env.DEV`).
	 */
	get isDev(): boolean {
		return this.metaDev;
	}
}

/**
 * Singleton instance for accessing environment variables
 */
export const env = new EnvConfig();

/**
 * Type-safe accessor functions for specific environment variables.
 *
 * Server-only accessors (`serverBackendUrl`, `internalBackendUrl`, `nodeEnv`,
 * `openRouterApiKey`, `openRouterBaseUrl`, `serverAppUrl`, `appName`) are
 * intentionally absent because they read secrets that have no place in a
 * client bundle. If a caller imports them, the missing-property TypeScript
 * error surfaces the issue so it can be ported to a backend call.
 *
 * Server-only env accessors stay out of the client bundle; consumers that need
 * those values must call a backend endpoint.
 */
export const envVars = {
	/**
	 * Backend URL for API calls (browser/client-side)
	 */
	get backendUrl(): string | undefined {
		return env.get("VITE_BACKEND_URL");
	},

	/**
	 * App URL (browser/client-side, falls back to constants default)
	 */
	get appUrl(): string {
		return env.getOrDefault("VITE_APP_URL", DEFAULT_APP_URL);
	},

	/**
	 * Timezone (browser/client-side)
	 */
	get timezone(): string | undefined {
		return env.get("VITE_TIMEZONE");
	},

	/**
	 * Log level (browser/client-side)
	 */
	get logLevel(): string | undefined {
		return env.get("VITE_LOG_LEVEL");
	},

	/**
	 * Timezone (uses constant default)
	 */
	get defaultTimezone(): string {
		return env.getOrDefault("VITE_TIMEZONE", TIMEZONE);
	},

	/**
	 * Event debug disabled flag (uses constant default)
	 */
	get disableEventDebug(): boolean {
		return env.getBoolean("VITE_DISABLE_EVENT_DEBUG", DISABLE_EVENT_DEBUG);
	},

	/**
	 * Google Maps JavaScript API key (browser/client-side). `undefined` when
	 * unset, in which case the location picker renders a "not configured" state.
	 */
	get googleMapsApiKey(): string | undefined {
		return env.get("VITE_GOOGLE_MAPS_API_KEY");
	},

	/**
	 * Google Maps Map ID for vector maps + Advanced Markers. Falls back to
	 * Google's public `DEMO_MAP_ID` (fine for development; create a real Map ID
	 * in Google Cloud Console for production styling + quota isolation).
	 */
	get googleMapsMapId(): string {
		return env.getOrDefault("VITE_GOOGLE_MAPS_MAP_ID", "DEMO_MAP_ID");
	},

	/** Whether the Google Maps API key is configured. */
	get hasGoogleMaps(): boolean {
		return env.has("VITE_GOOGLE_MAPS_API_KEY");
	},
} as const;
